/**
 * Company Sites Service
 * Handles all API calls related to company management
 * Uses fetchWithAuth for JWT + refresh token on 401
 */

import { fetchWithAuth } from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Service class for Company Sites API calls
 */
class CompanySitesService {
  /**
   * Get state list for company forms
   * @returns {Promise<Array<{ stateId: number, stateCode: string, stateName: string }>>}
   */
  static async getStateList() {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/state-list`;
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Failed to fetch state list: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching state list:', error);
      throw error;
    }
  }

  /**
   * Get all companies
   * @returns {Promise<Array>} List of companies (each may include companyId, companyName, companyMailId, companyStatus, startDate, endDate, stateId)
   * API response: { totalCount, companies: [...] }
   */
  static async getCompanies() {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/get-all-companies-admin`;

      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Failed to fetch companies: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.companies && Array.isArray(data.companies)) {
        return data.companies;
      }
      if (data.data && Array.isArray(data.data)) {
        return data.data;
      }
      if (Array.isArray(data)) {
        return data;
      }

      return [];
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
  }

  /**
   * Create a new company
   * @param {Object} companyData
   * @param {string} companyData.companyName - Required
   * @param {string} [companyData.companyMailId] - Optional, valid email if provided
   * @param {number} companyData.stateId - Required
   * @returns {Promise<Object>} Created company (message, companyId, companyName, companyMailId, companyStatus, startDate, stateId)
   */
  static async createCompany(companyData) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/create-company`;

      const response = await fetchWithAuth(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: companyData.companyName,
          ...(companyData.companyMailId && { companyMailId: companyData.companyMailId }),
          stateId: Number(companyData.stateId),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to create company: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating company:', error);
      throw error;
    }
  }

  /**
   * Delete a company
   * @param {number} companyId - Company ID to delete
   * @returns {Promise<Object>} Response message
   */
  static async deleteCompany(companyId) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/${companyId}`;
      
      const response = await fetchWithAuth(apiUrl, { method: 'DELETE' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `Failed to delete company: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  }

  /**
   * Get all sites for a company
   * @param {number} companyId - Company ID
   * @returns {Promise<Object>} Response with sites data
   * Expected response format: { message, companyId, companyName, totalCount, sites: [{ siteId, siteName, siteAddress, siteStatus, startDate, endDate }] }
   */
  static async getCompanySites(companyId) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/get-all-sites?companyId=${companyId}`;
      
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Failed to fetch sites: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching company sites:', error);
      throw error;
    }
  }

  /**
   * Create a new site for a company
   * @param {Object} siteData - Site data
   * @param {number} siteData.companyId - Company ID (required)
   * @param {string} siteData.siteName - Site name (required)
   * @param {string} siteData.siteAddress1 - Address line 1 (required, max 100 chars)
   * @param {string} [siteData.siteAddress2] - Address line 2 (max 100 chars)
   * @param {string} siteData.landmark - Landmark (required, max 50 chars)
   * @param {string} siteData.city - City (required, max 30 chars)
   * @param {string} siteData.pincode - Pincode (required, max 20 chars)
   * @returns {Promise<Object>} Created site data
   * Expected response format: { message, siteId, companyId, companyName, siteName, siteAddress, siteAddress1, siteAddress2, landmark, city, pincode, siteStatus, startDate }
   */
  static async createSite(siteData) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/create-site`;
      
      const response = await fetchWithAuth(apiUrl, {
        method: 'POST',
        body: JSON.stringify(siteData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `Failed to create site: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating site:', error);
      throw error;
    }
  }

  /**
   * Delete a site
   * @param {number} siteId - Site ID to delete
   * @returns {Promise<Object>} Response message
   */
  static async deleteSite(siteId) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/site/${siteId}`;
      
      const response = await fetchWithAuth(apiUrl, { method: 'DELETE' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `Failed to delete site: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting site:', error);
      throw error;
    }
  }

  /**
   * Update a company
   * @param {Object} companyData
   * @param {number} companyData.companyId - Required
   * @param {string} companyData.companyName - Required
   * @param {string} [companyData.companyMailId] - Optional, valid email if provided
   * @param {number} companyData.stateId - Required
   * @returns {Promise<Object>} Updated company (message, companyId, companyName, companyMailId, companyStatus, startDate, updatedAt, stateId)
   */
  static async updateCompany(companyData) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/update-company`;

      const response = await fetchWithAuth(apiUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: Number(companyData.companyId),
          companyName: companyData.companyName,
          ...(companyData.companyMailId != null && companyData.companyMailId !== '' && { companyMailId: companyData.companyMailId }),
          stateId: Number(companyData.stateId),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to update company: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  }

  /**
   * Update a site
   * @param {Object} siteData - Site data to update
   * @param {number} siteData.siteId - Site ID (required)
   * @param {string} siteData.siteName - Updated site name (required)
   * @param {string} siteData.siteAddress1 - Address line 1 (required, max 100 chars)
   * @param {string} [siteData.siteAddress2] - Address line 2 (max 100 chars)
   * @param {string} siteData.landmark - Landmark (required, max 50 chars)
   * @param {string} siteData.city - City (required, max 30 chars)
   * @param {string} siteData.pincode - Pincode (required, max 20 chars)
   * @returns {Promise<Object>} Updated site data
   * Expected response format: { message, siteId, companyId, companyName, siteName, siteAddress, siteAddress1, siteAddress2, landmark, city, pincode, siteStatus, startDate, updatedAt }
   */
  static async updateSite(siteData) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/update-site`;
      
      const response = await fetchWithAuth(apiUrl, {
        method: 'PUT',
        body: JSON.stringify(siteData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `Failed to update site: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating site:', error);
      throw error;
    }
  }

  /**
   * Get all sub-companies (contractors) for a parent company
   * @param {number} companyId - Parent company ID
   * @returns {Promise<Object>} { companyId, companyName, totalCount, subCompanies: [...] }
   */
  static async getSubCompanies(companyId) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/get-all-sub-companies?companyId=${companyId}`;

      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to fetch contractors: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching sub-companies:', error);
      throw error;
    }
  }

  /**
   * Create a sub-company (contractor) under a parent company
   * @param {Object} data
   * @param {number} data.companyId - Parent company ID
   * @param {string} data.companyName - Required
   * @param {string} [data.companyMailId] - Optional
   * @returns {Promise<Object>} Created sub-company
   */
  static async createSubCompany(data) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/create-sub-company`;

      const response = await fetchWithAuth(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: Number(data.companyId),
          companyName: data.companyName,
          ...(data.companyMailId && { companyMailId: data.companyMailId }),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to create contractor: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating sub-company:', error);
      throw error;
    }
  }

  /**
   * Update a sub-company (contractor)
   * @param {Object} data
   * @param {number} data.companyId - Sub-company ID
   * @param {string} data.companyName - Required
   * @param {string} [data.companyMailId] - Optional
   * @returns {Promise<Object>} Updated sub-company
   */
  static async updateSubCompany(data) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/update-sub-company`;

      const response = await fetchWithAuth(apiUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: Number(data.companyId),
          companyName: data.companyName,
          ...(data.companyMailId != null && data.companyMailId !== '' && { companyMailId: data.companyMailId }),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to update contractor: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating sub-company:', error);
      throw error;
    }
  }
}

export default CompanySitesService;

